import React from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ActionPlanItem, ProjectFormData } from '../types/project-form';

interface ActionPlanProps {
  formData: ProjectFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProjectFormData>>;
}

const ActionPlan: React.FC<ActionPlanProps> = ({
  formData,
  setFormData
}) => {
  const handleActionChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].action = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleResponsiblePersonChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].responsiblePerson = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleResourcesChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].resources = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleByWhenChange = (index: number, value: string) => {
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const updatedPlan = [...(formData.actionPlan || [])];

    if (selectedDate < today) {
      // Set error message on the item
      updatedPlan[index] = {
        ...updatedPlan[index],
        byWhen: value,
        byWhenError: 'Date cannot be in the past'
      };
    } else {
      // Clear error if previously set
      updatedPlan[index] = {
        ...updatedPlan[index],
        byWhen: value,
        byWhenError: undefined
      };
    }

    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handlePlaceChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].place = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleMovChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].mov = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleDeleteAction = (index: number) => {
    const updatedPlan = formData.actionPlan?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleRootCauseChange = (index: number, value: string) => {
    const updatedPlan = [...(formData.actionPlan || [])];
    updatedPlan[index].rootCause = value;
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };

  const handleAddAction = () => {
    const sortedRootCauses = getSortedRootCauses();
    const currentPlanLength = formData.actionPlan?.length || 0;
    
    // Auto-assign the next available root cause based on ranking
    const nextRootCause = sortedRootCauses[currentPlanLength] || null;
    
    const newItem: ActionPlanItem = {
      action: '',
      responsiblePerson: '',
      resources: '',
      byWhen: '',
      place: '',
      mov: '',
      rootCause: nextRootCause ? nextRootCause.value : ''
    };

    const updatedPlan = [...(formData.actionPlan || []), newItem];
    setFormData(prev => ({ ...prev, actionPlan: updatedPlan }));
  };


  // Get sorted root causes from fishbone analysis (highest ranked causes from each category)
  const getSortedRootCauses = () => {
    if (!formData.fishBoneAnalysis) {
      return [];
    }
    
    const rootCauses: Array<{value: string, label: string, rank: number, category: string}> = [];
    
    // Define the categories to check
    const categories = [
      { key: 'peopleCauses', name: 'People' },
      { key: 'processCauses', name: 'Process' },
      { key: 'policyCauses', name: 'Policy' },
      { key: 'placeCauses', name: 'Place' },
      { key: 'provisionCauses', name: 'Provision' }
    ];
    
    categories.forEach(category => {
      const categoryData = formData.fishBoneAnalysis?.[category.key as keyof typeof formData.fishBoneAnalysis];
      
      if (categoryData?.causeItems && Array.isArray(categoryData.causeItems)) {
        // Find the highest ranked cause (rank 1) in this category
        const topRankedCause = categoryData.causeItems.find((cause: any) => cause.rank === 1);
        
        if (topRankedCause && topRankedCause.whyAnalysis) {
          // Get the last why answer (root cause) - check reason5 first, then fallback to other reasons
          const rootCause = topRankedCause.whyAnalysis.reason5 || 
                           topRankedCause.whyAnalysis.reason4 || 
                           topRankedCause.whyAnalysis.reason3 || 
                           topRankedCause.whyAnalysis.reason2 || 
                           topRankedCause.whyAnalysis.reason1 ||
                           topRankedCause.whyAnalysis.rootCause;
          
          if (rootCause && rootCause.trim() !== '') {
            rootCauses.push({
              value: rootCause,
              label: `${category.name}: ${rootCause}`,
              rank: topRankedCause.rank || 1, // Fallback to 1 if rank is undefined
              category: category.name
            });
          }
        }
      }
    });
    
    // Sort by category order (People, Process, Policy, Place, Provision)
    return rootCauses.sort((a, b) => {
      const categoryOrder = ['People', 'Process', 'Policy', 'Place', 'Provision'];
      return categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
    });
  };

  // Get all available team members for the dropdown
  const getTeamMembers = () => {
    const members = [];
    
    // Add CHS team members
    if (formData.chsTeamMembers) {
      formData.chsTeamMembers.forEach((member, index) => (
        members.push({
          key: `chs-${index}`,
          value: member.fullName,
          label: `${member.fullName} (${member.role})`
        })
      ));
    }
    
    // Add participants
    if (formData.participants) {
      formData.participants.forEach((participant, index) => (
        members.push({
          key: `participant-${index}`,
          value: participant.fullName,
          label: `${participant.fullName} (${participant.role})`
        })
      ));
    }
    
    // Add project lead
    if (formData.responsiblePerson) {
      members.push({
        key: 'project-lead',
        value: formData.responsiblePerson,
        label: `${formData.responsiblePerson} (Project Lead)`
      });
    }
    
    return members;
  };


  return (
    <Box sx={{ width: '100%', mt: 4, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Change Plan
      </Typography>
      
      <TableContainer component={Paper}>
        <Table sx={{
          tableLayout: 'fixed',
          minWidth: 1200,
          '& .MuiTableCell-root': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            verticalAlign: 'top'
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell>Root Cause</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Person Responsible</TableCell>
              <TableCell>
                Resources
                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Specify all inputs required (e.g., funding, materials, tools, personnel, logistics)
                </Typography>
              </TableCell>
              <TableCell>By When</TableCell>
              <TableCell>
                Place
                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Indicate the location at which the action will apply, e.g., household, village
                </Typography>
              </TableCell>
              <TableCell>
                MoV
                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Means of Verification - evidence or source to confirm the action was done
                </Typography>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(formData.actionPlan || []).map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Select
                    fullWidth
                    value={item.rootCause || ''}
                    onChange={(e) => handleRootCauseChange(index, e.target.value)}
                    variant="standard"
                    displayEmpty
                    sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                  >
                    <MenuItem value="">
                      <em>Select root cause</em>
                    </MenuItem>
                    {getSortedRootCauses().map((rootCause, rcIndex) => (
                      <MenuItem key={rcIndex} value={rootCause.value}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'medium',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word'
                        }}>
                          {rootCause.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    value={item.action}
                    onChange={(e) => handleActionChange(index, e.target.value)}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <Select
                    fullWidth
                    value={item.responsiblePerson}
                    onChange={(e) => handleResponsiblePersonChange(index, e.target.value)}
                    variant="standard"
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select team member</em>
                    </MenuItem>
                    {getTeamMembers().map((member) => (
                      <MenuItem key={member.key} value={member.value}>
                        {member.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    value={item.resources}
                    onChange={(e) => handleResourcesChange(index, e.target.value)}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    type="date"
                    value={item.byWhen}
                    onChange={(e) => handleByWhenChange(index, e.target.value)}
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    error={!!item.byWhenError}
                    helperText={item.byWhenError}
                    // Set min attribute to today's date to prevent past selection
                    inputProps={{
                      min: new Date().toISOString().split('T')[0]
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    value={item.place}
                    onChange={(e) => handlePlaceChange(index, e.target.value)}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    value={item.mov || ''}
                    onChange={(e) => handleMovChange(index, e.target.value)}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleDeleteAction(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 2 }}
        onClick={handleAddAction}
      >
        Add Change
      </Button>

    </Box>
  );
};

export default ActionPlan;
